"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  InstagramLogoIcon,
  LinkedInLogoIcon,
  VideoIcon,
} from "@radix-ui/react-icons";
import { MapPin, Phone, Mail, Clock, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  FacebookBrandIcon,
  GoogleBrandIcon,
  PinterestBrandIcon,
  TikTokBrandIcon,
  XBrandIcon,
  YelpBrandIcon,
} from "@/components/icons/social-brand-icons";
import { analytics } from "@/lib/analytics";
import {
  isOrderFeatureEnabled,
  resolveGuestFacingOrderHref,
} from "@/lib/features";
import { navLinkTransitionTypes } from "@/lib/nav-transition-types";
import {
  getPublicChromeForPathname,
  type PublicChromeLink,
  type PublicChromeSocialLink,
} from "@/lib/public-chrome";
import {
  romaBranchLocations,
  type RomaBranchLocation,
} from "@/lib/restaurant-platform";
import { useBrowserPublicHost } from "@/lib/use-browser-public-host";
import { useBrowserPublicLocationId } from "@/lib/use-browser-public-location-id";
import {
  getPublicPageBrand,
  type PublicPageBrand,
} from "@/lib/public-brand-theme";
// Direct module import; the "@/components/roma" barrel would pull the Roma
// funnel homepage into the shared public chrome bundle (see public-chrome.tsx).
import { useRomaReserveSheet } from "@/components/roma/roma-reserve-sheet-context";
import {
  RomaLocationOrderLink,
  RomaOrderCta,
} from "@/components/roma/roma-order-chooser";

// Footer "best" guide links per tenant brand. Each href MUST exist in that
// tenant's money-page corpus (getMoneyPagesForConfig) or it 404s on that host.
const PUBLIC_BEST_GUIDES: Record<
  string,
  { href: string; label: string; ariaLabel: string }[]
> = {
  "tuscan-cove": [
    {
      href: "/best/restaurants-southern-highlands-las-vegas",
      label: "Restaurants",
      ariaLabel: "Southern Highlands restaurants",
    },
    {
      href: "/best/happy-hour-southern-highlands-las-vegas",
      label: "Happy hour",
      ariaLabel: "Happy hour in Southern Highlands",
    },
    {
      href: "/best/pizza-southern-highlands-las-vegas",
      label: "Pizza",
      ariaLabel: "Pizza in Southern Highlands",
    },
    {
      href: "/best/brunch-southern-highlands-las-vegas",
      label: "Brunch",
      ariaLabel: "Brunch in Southern Highlands",
    },
    {
      href: "/best/burgers-southern-highlands-las-vegas",
      label: "Burgers",
      ariaLabel: "Burgers in Southern Highlands",
    },
    {
      href: "/best/restaurants-near-south-point-casino",
      label: "Near South Point",
      ariaLabel: "Restaurants near South Point Casino",
    },
    {
      href: "/best/private-dining-las-vegas",
      label: "Private dining",
      ariaLabel: "Private dining in Las Vegas",
    },
    {
      href: "/best/corporate-catering-las-vegas",
      label: "Corporate catering",
      ariaLabel: "Corporate catering in Las Vegas",
    },
    {
      href: "/best/catering-southern-highlands-las-vegas",
      label: "Catering",
      ariaLabel: "Catering in Southern Highlands",
    },
  ],
  "chefs-roma": [
    {
      href: "/best/italian-restaurant-henderson-nv",
      label: "Henderson",
      ariaLabel: "Italian restaurant in Henderson NV",
    },
    {
      href: "/best/italian-restaurant-southern-highlands-las-vegas",
      label: "Southern Highlands",
      ariaLabel: "Italian restaurant in Southern Highlands, Las Vegas",
    },
    {
      href: "/best/pasta-las-vegas",
      label: "Pasta",
      ariaLabel: "Pasta in Las Vegas",
    },
    {
      href: "/best/italian-catering-las-vegas",
      label: "Catering",
      ariaLabel: "Italian catering in Las Vegas",
    },
    {
      href: "/best/private-dining-henderson-las-vegas",
      label: "Private dining",
      ariaLabel: "Private dining in Henderson and Las Vegas",
    },
    {
      href: "/holiday-parties",
      label: "Holiday parties",
      ariaLabel: "Holiday parties at Chef's Roma Kitchen",
    },
    {
      href: "/best/italian-restaurant-off-the-strip-las-vegas",
      label: "Off-Strip Italian",
      ariaLabel: "Italian restaurant off the Strip in Las Vegas",
    },
  ],
};

// Chef's Roma Kitchen lists both rooms in the footer. Henderson is a branch
// location with no host of its own, so its name, address, phone, and hours
// come straight from the canonical branch data in restaurant-platform.ts.
// Rendering is gated on chromeBrand === "chefs-roma"; Tuscan never sees this.
const ROMA_HENDERSON_BRANCH = romaBranchLocations.find(
  (branch) => branch.id === "roma-kitchen-henderson",
);

// Mirrors public-chrome's private formatTime so branch hours render in the
// same style as chrome.hoursLines.
function formatBranchTime(time: string): string {
  const [hourPart, minutePart = "00"] = time.split(":");
  const hour = Number(hourPart);
  const minute = Number(minutePart);

  if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
    return time;
  }

  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const displayMinute = minute === 0 ? "" : `:${String(minute).padStart(2, "0")}`;

  return `${displayHour}${displayMinute} ${period}`;
}

function buildBranchHoursLines(hours: RomaBranchLocation["hours"]): string[] {
  return hours.weekly.map((entry) => {
    const first = entry.days[0]?.slice(0, 3) ?? "";
    const last = entry.days[entry.days.length - 1]?.slice(0, 3) ?? "";
    const range = entry.days.length > 1 ? `${first}-${last}` : first;
    return `${range} ${formatBranchTime(entry.opens)}-${formatBranchTime(entry.closes)}`;
  });
}

// Mirrors public-chrome's private formatPhone.
function formatBranchPhone(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  const normalized =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;

  if (normalized.length !== 10) {
    return phone;
  }

  return `(${normalized.slice(0, 3)}) ${normalized.slice(3, 6)}-${normalized.slice(6)}`;
}

// Same query shape as restaurant-platform's buildMapsHrefForLocation, which
// only accepts full PlatformLocation entries.
function buildBranchMapsHref(branch: RomaBranchLocation): string {
  const addressText = `${branch.name} ${branch.address.line1} ${branch.address.city} ${branch.address.state} ${branch.address.zip}`;

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    addressText,
  )}`;
}

function getCtaTrackingProps(
  pathname: string,
  location: string,
  ctaText: string,
  destinationUrl: string
) {
  return {
    location,
    cta_text: ctaText,
    destination_url: destinationUrl,
    page_path: pathname,
  };
}

function FooterChromeLink({
  link,
  pathname,
  location,
  useRomaOrderChooser,
}: {
  link: PublicChromeLink;
  pathname: string;
  location: string;
  useRomaOrderChooser?: boolean;
}) {
  const className =
    "roma-text-muted inline-flex min-h-9 items-center text-sm leading-tight transition-colors hover:text-[var(--roma-terracotta)]";
  const handleClick = () => {
    const tracking = getCtaTrackingProps(
      pathname,
      location,
      link.label,
      link.href
    );

    if (link.kind === "order") analytics.orderStarted(tracking);
    if (link.kind === "menu") analytics.menuBrowseClicked(tracking);
    if (link.kind === "directions" || link.href.includes("google.com/maps")) {
      analytics.directionsClicked(tracking);
    }
    analytics.ctaClicked(link.label, location, tracking);
  };

  if (useRomaOrderChooser && link.kind === "order") {
    return (
      <RomaOrderCta
        trackingLocation={location}
        triggerLabel={link.label}
        className={className}
        pathname={pathname}
      >
        {link.label}
      </RomaOrderCta>
    );
  }

  if (link.external) {
    return (
      <a
        href={link.href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        onClick={handleClick}
      >
        {link.label}
      </a>
    );
  }

  return (
    <Link
      href={link.href}
      transitionTypes={navLinkTransitionTypes(link.href, pathname)}
      className={className}
      onClick={handleClick}
    >
      {link.label}
    </Link>
  );
}

function FooterSocialIcon({
  platform,
}: {
  platform: PublicChromeSocialLink["platform"];
}) {
  const className = "h-5 w-5";

  switch (platform) {
    case "instagram":
      return <InstagramLogoIcon aria-hidden="true" className={className} />;
    case "facebook":
      return <FacebookBrandIcon className={className} />;
    case "yelp":
      return <YelpBrandIcon className={className} />;
    case "x":
      return <XBrandIcon className={className} />;
    case "youtube":
      return <VideoIcon aria-hidden="true" className={className} />;
    case "linkedin":
      return <LinkedInLogoIcon aria-hidden="true" className={className} />;
    case "pinterest":
      return <PinterestBrandIcon className={className} />;
    case "tiktok":
      return <TikTokBrandIcon className={className} />;
    case "google":
      return <GoogleBrandIcon className={className} />;
    default:
      return null;
  }
}

function FooterSocialLink({
  link,
  pathname,
  siteName,
}: {
  link: PublicChromeSocialLink;
  pathname: string;
  siteName: string;
}) {
  const ariaLabel = `${siteName} on ${link.label}`;

  return (
    <a
      href={link.href}
      target="_blank"
      rel="noopener noreferrer"
      className="roma-text-muted inline-flex h-11 w-11 items-center justify-center transition-colors hover:text-[var(--roma-terracotta)]"
      aria-label={ariaLabel}
      onClick={() =>
        analytics.socialClicked(
          link.platform,
          getCtaTrackingProps(pathname, "footer_social", link.label, link.href),
        )
      }
    >
      <FooterSocialIcon platform={link.platform} />
      <span className="sr-only">{ariaLabel}</span>
    </a>
  );
}

export function Footer({
  initialPublicHost,
  initialPublicLocationId,
  pageBrand,
  romaFunnelEnabled,
  draftPreview = false,
  cateringOrderingLive = false,
}: {
  initialPublicHost?: string;
  initialPublicLocationId?: string;
  pageBrand?: PublicPageBrand;
  romaFunnelEnabled?: boolean;
  draftPreview?: boolean;
  /** Server-resolved per request host; adds the "Order catering" entry when live. */
  cateringOrderingLive?: boolean;
} = {}) {
  const pathname = usePathname();
  const browserPublicHost = useBrowserPublicHost(initialPublicHost);
  const browserPublicLocationId = useBrowserPublicLocationId(initialPublicLocationId);
  const chrome = getPublicChromeForPathname(
    pathname ?? "",
    {
      NEXT_PUBLIC_SITE_URL: browserPublicHost,
      NEXT_PUBLIC_LOCATION_ID: browserPublicLocationId,
    },
    { pageBrand, romaFunnelEnabled, draftPreview, cateringOrderingLive },
  );
  const { siteConfig } = chrome;
  const reserveSheet = useRomaReserveSheet();

  if (pathname === "/catering") {
    return null;
  }

  const orderHref = resolveGuestFacingOrderHref({
    orderingMode: siteConfig.ordering.mode,
    orderUrl: siteConfig.orderUrl,
    guestOrderCta: siteConfig.guestOrderCta,
  });
  const isFirstPartyOrder = siteConfig.ordering.mode === "first_party";
  const showFirstPartyOrder =
    isFirstPartyOrder && isOrderFeatureEnabled() && Boolean(orderHref);
  const showExternalOrder = !isFirstPartyOrder && Boolean(orderHref);
  const orderIsExternal = orderHref.startsWith("http");
  const exploreAlreadyHasOrder = chrome.footerExploreLinks.some(
    (link) => link.kind === "order",
  );
  const externalOrderLabel = siteConfig.ordering.deliveryEnabled
    ? "Order pickup or delivery"
    : "Order pickup";
  const exploreLinks: PublicChromeLink[] =
    (showFirstPartyOrder || showExternalOrder) && !exploreAlreadyHasOrder
      ? [
          {
            kind: "order",
            href: orderHref,
            label: isFirstPartyOrder ? "Order online" : externalOrderLabel,
            external: orderIsExternal,
          },
          ...chrome.footerExploreLinks,
        ]
      : chrome.footerExploreLinks;
  const chromeBrand = getPublicPageBrand(siteConfig);
  const isRomaDarkFunnel =
    chromeBrand === "chefs-roma" && Boolean(romaFunnelEnabled);
  const guideLinks = PUBLIC_BEST_GUIDES[chromeBrand] ?? [];
  // Roma-only: surface the Henderson room's NAP so callers reach the right
  // room. Undefined for every other brand, so Tuscan renders unchanged.
  const hendersonBranch =
    chromeBrand === "chefs-roma" ? ROMA_HENDERSON_BRANCH : undefined;

  return (
    <footer
      data-public-chrome-brand={chromeBrand}
      className="roma-paper border-t border-[var(--roma-parchment-line)] md:pb-0"
    >
      <div className="container mx-auto max-w-7xl px-4 py-6 md:px-6 md:py-8">
        <div className="grid gap-8 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] md:items-start lg:gap-12">
          <div className="min-w-0 text-left">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <div className="flex items-center gap-2.5">
                <span
                  aria-hidden="true"
                  className="h-px w-7 shrink-0 bg-[var(--roma-terracotta)]/60"
                />
                <Link
                  href={chrome.brandHref}
                  transitionTypes={navLinkTransitionTypes(chrome.brandHref, pathname)}
                  className="font-serif text-xl font-normal tracking-tight text-[var(--foreground)] md:text-2xl"
                >
                  {siteConfig.name}
                </Link>
              </div>
              {chromeBrand === "chefs-roma" && reserveSheet?.enabled ? (
                <Button
                  type="button"
                  size="sm"
                  className={
                    isRomaDarkFunnel
                      ? "min-h-11 w-full rounded-xl border border-[#b91c1c] bg-[#b91c1c] px-4 text-sm font-semibold text-white shadow-none hover:border-[#cf1f1f] hover:bg-[#cf1f1f] sm:w-auto"
                      : "min-h-11 w-full rounded-xl border border-[var(--roma-terracotta)] bg-[var(--roma-terracotta)] px-4 text-sm font-semibold text-[#0d0d0d] shadow-none hover:bg-[var(--roma-saffron-deep)] sm:w-auto"
                  }
                  onClick={() => {
                    reserveSheet.openSheet();
                    const tracking = getCtaTrackingProps(
                      pathname,
                      "footer_brand",
                      "Reserve",
                      "/reservations#picker",
                    );
                    analytics.reservationClicked("footer_brand", tracking);
                    analytics.ctaClicked("Reserve", "footer_brand", tracking);
                  }}
                >
                  <Calendar className="mr-1.5 h-4 w-4" aria-hidden="true" />
                  Reserve
                </Button>
              ) : null}
            </div>

            {hendersonBranch ? (
              <div className="mt-5 space-y-3 sm:mt-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                  <div
                    className={
                      isRomaDarkFunnel
                        ? "rounded-2xl border border-white/12 bg-white/[0.03] px-3.5 py-3"
                        : "rounded-2xl border border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)]/40 px-3.5 py-3"
                    }
                  >
                    <a
                      href={chrome.mapsHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-2 transition-colors hover:text-[var(--roma-terracotta)]"
                      onClick={() => {
                        const tracking = getCtaTrackingProps(
                          pathname,
                          "footer_contact_grid",
                          "Get directions",
                          chrome.mapsHref,
                        );
                        analytics.directionsClicked(tracking);
                        analytics.ctaClicked(
                          "Get directions",
                          "footer_contact_grid",
                          tracking,
                        );
                      }}
                    >
                      <MapPin className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[var(--roma-terracotta)]" />
                      <div className="min-w-0">
                        <p className="text-xs font-semibold leading-tight text-[var(--foreground)]">
                          Southern Highlands
                        </p>
                        <p className="roma-text-muted mt-0.5 text-xs leading-snug">
                          {siteConfig.address.line1}
                        </p>
                        <p className="roma-text-muted text-xs leading-snug">
                          {siteConfig.address.city}, {siteConfig.address.state}{" "}
                          {siteConfig.address.zip}
                        </p>
                      </div>
                    </a>
                    <a
                      href={`tel:${siteConfig.contact.phone}`}
                      aria-label={`Call ${siteConfig.name} Southern Highlands at ${chrome.displayPhone}`}
                      className="mt-2 flex items-center gap-2 transition-colors hover:text-[var(--roma-terracotta)]"
                      onClick={() =>
                        analytics.phoneClicked(
                          getCtaTrackingProps(
                            pathname,
                            "footer_contact_grid",
                            `Call ${siteConfig.name} Southern Highlands`,
                            `tel:${siteConfig.contact.phone}`,
                          ),
                        )
                      }
                    >
                      <Phone className="h-3.5 w-3.5 flex-shrink-0 text-[var(--roma-terracotta)]" />
                      <span className="roma-text-muted inline-flex min-h-10 items-center text-xs font-medium">
                        {chrome.displayPhone}
                      </span>
                    </a>
                    <div
                      className={
                        isRomaDarkFunnel
                          ? "mt-2 flex items-start gap-2 border-t border-white/10 pt-2"
                          : "mt-2 flex items-start gap-2 border-t border-[var(--roma-parchment-line)] pt-2"
                      }
                    >
                      <Clock className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[var(--roma-terracotta)]" />
                      <div>
                        <p className="text-[11px] font-medium uppercase tracking-wide text-[var(--muted-foreground)]">
                          Hours
                        </p>
                        {chrome.hoursLines.map((line) => (
                          <p
                            key={line}
                            className="roma-text-muted text-xs leading-snug"
                          >
                            {line}
                          </p>
                        ))}
                      </div>
                    </div>
                    <RomaLocationOrderLink
                      slug="southern-highlands"
                      trackingLocation="footer_contact_grid"
                      className="mt-2 inline-flex min-h-10 items-center text-xs font-semibold text-[var(--roma-terracotta)] hover:underline"
                    />
                  </div>

                  <div
                    className={
                      isRomaDarkFunnel
                        ? "rounded-2xl border border-white/12 bg-white/[0.03] px-3.5 py-3"
                        : "rounded-2xl border border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)]/40 px-3.5 py-3"
                    }
                  >
                    <a
                      href={buildBranchMapsHref(hendersonBranch)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-2 transition-colors hover:text-[var(--roma-terracotta)]"
                      onClick={() => {
                        const tracking = getCtaTrackingProps(
                          pathname,
                          "footer_contact_grid",
                          "Get directions Henderson",
                          buildBranchMapsHref(hendersonBranch),
                        );
                        analytics.directionsClicked(tracking);
                        analytics.ctaClicked(
                          "Get directions Henderson",
                          "footer_contact_grid",
                          tracking,
                        );
                      }}
                    >
                      <MapPin className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[var(--roma-terracotta)]" />
                      <div className="min-w-0">
                        <p className="text-xs font-semibold leading-tight text-[var(--foreground)]">
                          Henderson
                        </p>
                        <p className="roma-text-muted mt-0.5 text-xs leading-snug">
                          {hendersonBranch.address.line1}
                        </p>
                        <p className="roma-text-muted text-xs leading-snug">
                          {hendersonBranch.address.city},{" "}
                          {hendersonBranch.address.state}{" "}
                          {hendersonBranch.address.zip}
                        </p>
                      </div>
                    </a>
                    <a
                      href={`tel:${hendersonBranch.phone}`}
                      aria-label={`Call ${hendersonBranch.name} at ${formatBranchPhone(hendersonBranch.phone)}`}
                      className="mt-2 flex items-center gap-2 transition-colors hover:text-[var(--roma-terracotta)]"
                      onClick={() =>
                        analytics.phoneClicked(
                          getCtaTrackingProps(
                            pathname,
                            "footer_contact_grid",
                            `Call ${hendersonBranch.name}`,
                            `tel:${hendersonBranch.phone}`,
                          ),
                        )
                      }
                    >
                      <Phone className="h-3.5 w-3.5 flex-shrink-0 text-[var(--roma-terracotta)]" />
                      <span className="roma-text-muted inline-flex min-h-10 items-center text-xs font-medium">
                        {formatBranchPhone(hendersonBranch.phone)}
                      </span>
                    </a>
                    <div
                      className={
                        isRomaDarkFunnel
                          ? "mt-2 flex items-start gap-2 border-t border-white/10 pt-2"
                          : "mt-2 flex items-start gap-2 border-t border-[var(--roma-parchment-line)] pt-2"
                      }
                    >
                      <Clock className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[var(--roma-terracotta)]" />
                      <div>
                        <p className="text-[11px] font-medium uppercase tracking-wide text-[var(--muted-foreground)]">
                          Hours
                        </p>
                        {buildBranchHoursLines(hendersonBranch.hours).map((line) => (
                          <p
                            key={line}
                            className="roma-text-muted text-xs leading-snug"
                          >
                            {line}
                          </p>
                        ))}
                      </div>
                    </div>
                    <RomaLocationOrderLink
                      slug="henderson"
                      trackingLocation="footer_contact_grid"
                      className="mt-2 inline-flex min-h-10 items-center text-xs font-semibold text-[var(--roma-terracotta)] hover:underline"
                    />
                  </div>
                </div>

                <a
                  href={`mailto:${siteConfig.contact.email}`}
                  className={
                    isRomaDarkFunnel
                      ? "flex min-h-11 items-center gap-2 rounded-2xl border border-white/12 bg-white/[0.03] px-3.5 py-2.5 transition-colors hover:text-[var(--roma-terracotta)]"
                      : "flex min-h-11 items-center gap-2 rounded-2xl border border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)]/40 px-3.5 py-2.5 transition-colors hover:text-[var(--roma-terracotta)]"
                  }
                >
                  <Mail className="h-3.5 w-3.5 flex-shrink-0 text-[var(--roma-terracotta)]" />
                  <span className="roma-text-muted break-all text-xs font-medium">
                    {siteConfig.contact.email}
                  </span>
                </a>
              </div>
            ) : (
              <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <a
                    href={chrome.mapsHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-1.5 transition-colors hover:text-[var(--roma-terracotta)]"
                    onClick={() => {
                      const tracking = getCtaTrackingProps(
                        pathname,
                        "footer_contact_grid",
                        "Get directions",
                        chrome.mapsHref,
                      );
                      analytics.directionsClicked(tracking);
                      analytics.ctaClicked(
                        "Get directions",
                        "footer_contact_grid",
                        tracking,
                      );
                    }}
                  >
                    <MapPin className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[var(--roma-terracotta)]" />
                    <div className="min-w-0">
                      <p className="text-xs font-medium leading-tight">Location</p>
                      <p className="roma-text-muted text-xs leading-snug">
                        {siteConfig.address.line1}
                      </p>
                      <p className="roma-text-muted text-xs leading-snug">
                        {siteConfig.address.city}, {siteConfig.address.state}{" "}
                        {siteConfig.address.zip}
                      </p>
                    </div>
                  </a>
                </div>
                <div className="flex items-start gap-1.5">
                  <Clock className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[var(--roma-terracotta)]" />
                  <div>
                    <p className="text-xs font-medium leading-tight">Hours</p>
                    {chrome.hoursLines.map((line) => (
                      <p key={line} className="roma-text-muted text-xs leading-snug">
                        {line}
                      </p>
                    ))}
                  </div>
                </div>
                <div className="flex items-start gap-1.5">
                  <Phone className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[var(--roma-terracotta)]" />
                  <div>
                    <p className="text-xs font-medium leading-tight">Phone</p>
                    <a
                      href={`tel:${siteConfig.contact.phone}`}
                      className="roma-text-muted -my-1 inline-flex min-h-11 items-center text-xs hover:text-[var(--roma-terracotta)]"
                      onClick={() =>
                        analytics.phoneClicked(
                          getCtaTrackingProps(
                            pathname,
                            "footer_contact_grid",
                            `Call ${siteConfig.name}`,
                            `tel:${siteConfig.contact.phone}`,
                          ),
                        )
                      }
                    >
                      {chrome.displayPhone}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-1.5">
                  <Mail className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[var(--roma-terracotta)]" />
                  <div className="min-w-0">
                    <p className="text-xs font-medium leading-tight">Email</p>
                    <a
                      href={`mailto:${siteConfig.contact.email}`}
                      className="roma-text-muted -my-1 inline-flex min-h-11 items-center break-all text-xs hover:text-[var(--roma-terracotta)]"
                    >
                      {siteConfig.contact.email}
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="grid min-w-0 grid-cols-2 gap-x-6 gap-y-6 md:justify-items-end md:text-right">
            <div className="min-w-0 flex flex-col items-start md:items-end">
              <h3 className="roma-eyebrow mb-1 text-[var(--roma-terracotta)]">
                Menu
              </h3>
              <ul className="flex flex-col items-start gap-0.5 md:items-end">
                {exploreLinks.map((link) => (
                  <li key={`${link.kind}-${link.href}`}>
                    <FooterChromeLink
                      link={link}
                      pathname={pathname}
                      location="footer_explore"
                      useRomaOrderChooser={chromeBrand === "chefs-roma"}
                    />
                  </li>
                ))}
              </ul>
            </div>
            <div className="min-w-0 flex flex-col items-start md:items-end">
              <h3 className="roma-eyebrow mb-1 text-[var(--roma-terracotta)]">
                More
              </h3>
              <ul className="flex flex-col items-start gap-0.5 md:items-end">
                {chrome.footerInfoLinks.map((link) => (
                  <li key={`${link.kind}-${link.href}`}>
                    <FooterChromeLink
                      link={link}
                      pathname={pathname}
                      location="footer_info"
                      useRomaOrderChooser={chromeBrand === "chefs-roma"}
                    />
                  </li>
                ))}
              </ul>
            </div>
            {chrome.socialLinks.length > 0 ? (
              <div className="col-span-2 flex min-w-0 flex-col items-center md:items-end">
                <h3 className="roma-eyebrow mb-2 text-[var(--roma-terracotta)]">
                  Follow Us
                </h3>
                <div className="flex flex-nowrap justify-center gap-1 md:justify-end">
                  {chrome.socialLinks.map((link) => (
                    <FooterSocialLink
                      key={`${link.platform}-${link.href}`}
                      link={link}
                      pathname={pathname}
                      siteName={siteConfig.name}
                    />
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {chrome.showSouthernHighlandsGuides && guideLinks.length > 0 ? (
          <div className="mt-7 border-t border-[var(--roma-parchment-line)] pt-4 md:mt-8 md:pt-5">
            <div className="flex flex-col gap-2 md:flex-row md:items-baseline md:gap-10">
              <h3 className="roma-eyebrow shrink-0 text-[var(--roma-terracotta)]">
                {chromeBrand === "tuscan-cove" ? "Southern Highlands guides" : "Local guides"}
              </h3>
              <ul className="flex flex-row flex-wrap gap-x-3 gap-y-0.5 md:gap-x-4">
                {guideLinks.map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      transitionTypes={navLinkTransitionTypes(link.href, pathname)}
                      aria-label={link.ariaLabel}
                      className="roma-text-muted inline-flex min-h-9 items-center text-sm leading-tight transition-colors hover:text-[var(--roma-terracotta)]"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}

        <div className="mt-6 flex flex-col gap-3 border-t border-[var(--roma-parchment-line)] pt-5 sm:flex-row sm:items-center sm:justify-between md:mt-7">
          <p className="roma-text-muted max-w-[30rem] text-xs leading-relaxed">
            © {new Date().getFullYear()} {siteConfig.legalName}. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs sm:justify-end">
            {chromeBrand === "chefs-roma" ? (
              <RomaOrderCta
                trackingLocation="footer_bottom_order"
                triggerLabel="Order"
                pathname={pathname}
                className="inline-flex min-h-8 items-center text-[var(--muted-foreground)] transition-colors hover:text-[var(--roma-terracotta)]"
              >
                Order
              </RomaOrderCta>
            ) : showFirstPartyOrder && !orderIsExternal ? (
              <Link
                href={orderHref}
                transitionTypes={navLinkTransitionTypes(orderHref, pathname)}
                className="inline-flex min-h-8 items-center text-[var(--muted-foreground)] transition-colors hover:text-[var(--roma-terracotta)]"
                onClick={() =>
                  analytics.orderStarted(
                    getCtaTrackingProps(
                      pathname,
                      "footer_bottom_order",
                      "Order online",
                      orderHref
                    )
                  )
                }
              >
                Order online
              </Link>
            ) : showExternalOrder || (showFirstPartyOrder && orderIsExternal) ? (
              <a
                href={orderHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-8 items-center text-[var(--muted-foreground)] transition-colors hover:text-[var(--roma-terracotta)]"
                onClick={() =>
                  analytics.orderStarted(
                    getCtaTrackingProps(
                      pathname,
                      "footer_bottom_order",
                      isFirstPartyOrder ? "Order online" : externalOrderLabel,
                      orderHref
                    )
                  )
                }
              >
                {isFirstPartyOrder ? "Order online" : externalOrderLabel}
              </a>
            ) : null}
            <Link
              href="/privacy"
              transitionTypes={navLinkTransitionTypes("/privacy", pathname)}
              className="roma-text-muted inline-flex min-h-8 items-center hover:text-[var(--roma-terracotta)]"
            >
              Privacy
            </Link>
            <Link
              href="/terms"
              transitionTypes={navLinkTransitionTypes("/terms", pathname)}
              className="roma-text-muted inline-flex min-h-8 items-center hover:text-[var(--roma-terracotta)]"
            >
              Terms
            </Link>
            <Link
              href="/accessibility"
              transitionTypes={navLinkTransitionTypes("/accessibility", pathname)}
              className="roma-text-muted inline-flex min-h-8 items-center hover:text-[var(--roma-terracotta)]"
            >
              Accessibility
            </Link>
            <a
              href="https://paxom.ai"
              target="_blank"
              rel="noopener"
              aria-label="Powered by Paxom (opens in a new tab)"
              className="inline-flex min-h-8 basis-full items-center font-semibold text-[var(--foreground)] transition-colors hover:text-[var(--roma-terracotta)] sm:basis-auto"
            >
              Powered by Paxom
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
